import a from '@/utils/axios/axios'
import { filterEvents } from '@/utils/filter/filterEvents'
import { pointFromEvent } from '@/utils/filter/pointFromEvent'
import Vue from 'vue'

// Simple LCG for deterministic random
function seededRandom(seed) {
  let m = 0x80000000, a = 1103515245, c = 12345
  let state = seed
  return function() {
    state = (a * state + c) % m
    return state / m
  }
}

const filter = {
  namespaced: true,
  state: () => ({
    filters: [],
    filter: null,
    loading: false,
    events: [],
    openedEvent: null,
    buffer: 0,
    end_buffer: 0,
    event: null,
    games: [],
    options: null,
    player_key: 0,
    selected_feed: 'primary',
    user_opened_event: false,
    opened_map_type: 'field', // field or plate
    maps_open: true,
    // Pagination state
    games_offset: 0,
    games_limit: 5,
    has_more_games: false,
    loading_more_games: false,
    is_quality_control: false,
    quality_control_view: null,
    quality_control_event_key: null,
    quality_control_percentage_checked: null,
    quality_control_analysis_phase: null
  }),
  mutations: {
    SET_IS_QUALITY_CONTROL(state, value) {
      state.is_quality_control = value
    },
    SET_OPENED_MAP_TYPE(state, type) {
      state.opened_map_type = type
    },
    SET_MAPS_OPEN(state, value) {
      state.maps_open = value
    },
    RESET(state) {
      state.filters = []
      state.filter = null
      state.loading = false
      state.events = []
      state.openedEvent = null
      state.event = null
      state.games = []
      state.options = null
      state.player_key = 0
      state.selected_feed = 'primary'
      state.user_opened_event = false
      // Reset pagination state
      state.games_offset = 0
      state.games_limit = 5
      state.has_more_games = false
      state.loading_more_games = false
    },
    SET_USER_OPENED_EVENT(state, value) {
      state.user_opened_event = value
    },
    UPDATE_PLAYER_KEY(state) {
      state.player_key++
    },
    SET_SELECTED_FEED(state, feed) {
      state.selected_feed = feed
    },
    SET_OPTIONS(state, options) {
      state.options = options
    },
    ADD_FILTER(state, filter) {
      state.filters = [...state.filters].concat(filter)
    },
    SET_FILTERS(state, filters) {
      state.filters = filters
    },
    SET_FILTER(state, filter) {
      let mod = [...state.filters]
      let i = mod.findIndex(f => f.filter_id == filter.filter_id)

      mod[i] = filter

      state.filters = mod
    },
    REMOVE_FILTER(state, id) {
      state.filters = [...state.filters].filter(f => f.filter_id != id)
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_LOADING_GAME(state, { game_id, loading }) {
      const mod = [...state.games]
      const i = mod.findIndex(g => g.game.id === game_id)
      mod[i]['loading'] = loading
      state.games = mod
    },
    SET_GAME_EVENTS(state, { game_id, events }) {
      const mod = [...state.games]
      const i = mod.findIndex(g => g.game.id === game_id)
      mod[i]['events'] = events
      state.games = mod
    },
    SET_OPENED_EVENT(state, event) {
      state.openedEvent = event
    },
    SET_BUFFER(state, buffer) {
      state.buffer = buffer
    },
    SET_CURRENT_FILTER(state, filter) {
      state.filter = filter
    },
    SET_END_BUFFER(state, value) {
      state.end_buffer = value
    },
    SET_PLAYING_EVENT(state, event) {
      state.event = event
    },
    SET_GAMES(state, games) {
      state.games = games
    },
    // Pagination mutations
    SET_GAMES_OFFSET(state, offset) {
      state.games_offset = offset
    },
    SET_HAS_MORE_GAMES(state, hasMore) {
      state.has_more_games = hasMore
    },
    SET_LOADING_MORE_GAMES(state, loading) {
      state.loading_more_games = loading
    },
    ADD_GAMES(state, games) {
      state.games = [...state.games, ...games]
    },
    SET_QUALITY_CONTROL_VIEW(state, value) {
      state.quality_control_view = value
    },
    SET_QUALITY_CONTROL_EVENT_KEY(state, value) {
      state.quality_control_event_key = value
    },
    SET_QUALITY_CONTROL_PERCENTAGE_CHECKED(state, value) {
      state.quality_control_percentage_checked = value
    },
    SET_QUALITY_CONTROL_ANALYSIS_PHASE(state, value) {
      state.quality_control_analysis_phase = value
    },
  },
  actions: {
    fetchGames(_, { id, filter, offset, limit }) {
      return new Promise((resolve, reject) => {
        a.put(`/filter2/games?offset=${offset}&limit=${limit}`, {
          filter_id: id,
          filter
        })
          .then(e => {
            let games = e.data.games.map(game => {
              return {
                game,
                loading: true,
                events: []
              }
            })
            resolve({
              games,
              filter: e.data.filter,
              has_more_games: e.data.has_more_games
            })
          })
          .catch(e => reject(e))
      })
    },
    getGameEvents({ commit }, { game_id }) {
      return new Promise((resolve, reject) => {
        a.get(`/filter2/game/${game_id}/events`)
          .then(e => {
            commit('SET_GAME_EVENTS', { game_id, events: e.data })
            resolve(e.data)
          })
          .catch(e => reject(e))
          .finally(() => {
            commit('SET_LOADING_GAME', { game_id, loading: false })
          })
      })
    },
    initFilters({ commit, rootGetters }) {
      const team_id = rootGetters['user/currentTeamId']
      commit('SET_LOADING', true)
      return new Promise((resolve, reject) => {
        a.get(`/filter2/team/${team_id}`)
          .then(res => {
            commit('SET_FILTERS', res.data)
            resolve(res)
          })
          .catch(e => {
            reject(e)
          })
          .finally(() => {
            // The first game that fetches data sets this to false
            commit('SET_LOADING', false)
          })
      })
    },
    updateTitle({ commit, rootGetters }, filter) {
      filter['team_id'] = rootGetters['user/currentTeamId']

      return new Promise((resolve, reject) => {
        a.put(`/filter2/${filter.id}`, filter)
          .then(res => {
            commit('SET_FILTER', res.data)
            resolve(res)
          })
          .catch(e => {
            reject(e)
          })
      })
    },
    deleteFilter({ commit }, id) {
      return new Promise((resolve, reject) => {
        a.delete(`/filter2/${id}`)
          .then(res => {
            commit('REMOVE_FILTER', id)
            resolve(res)
          })
          .catch(e => {
            reject(e)
          })
      })
    },
    postFilter({ commit }, filter) {
      return new Promise((resolve, reject) => {
        a.post('/filter2', filter)
          .then(res => {
            commit('ADD_FILTER', res.data)
            resolve(res)
          })
          .catch(e => {
            reject(e)
          })
      })
    },
    getFilterGames({ commit, dispatch }, { id, filter, offset = 0, limit = 5 }) {
      commit('RESET')
      commit('SET_LOADING', true)
      commit('SET_GAMES', [])
      commit('SET_CURRENT_FILTER', null)
      commit('SET_GAMES_OFFSET', 0)

      return dispatch('fetchGames', { id, filter, offset, limit })
        .then(({ games, filter: filterData, has_more_games }) => {
          commit('SET_GAMES', games)
          commit('SET_CURRENT_FILTER', filterData)
          commit('SET_HAS_MORE_GAMES', has_more_games)
          commit('SET_GAMES_OFFSET', offset + games.length)
          commit('SET_LOADING', false)
          return { games, filter: filterData, has_more_games }
        })
        .catch(e => {
          commit('SET_LOADING', false)
          throw e
        })
    },
    openEvent({ commit, getters }, payload) {
      const event = payload?.event || payload
      let feed = payload?.feed || getters.selected_feed
      const secondaryAvailable = getters.secondaryFeedAvailable(event)
      if(feed === 'secondary' && !secondaryAvailable) feed = 'primary'

      commit('SET_SELECTED_FEED', feed)
      commit('SET_OPENED_EVENT', event)
      const end_buffer = 0
      const buffer = getters.is_quality_control ? 1 : (getters.buffer || 2)
      const offset = feed === 'secondary' ? getters.secondaryFeedSyncOffset(event) : 0
      const source = feed === 'secondary' ? getters.secondaryFeedSource(event) : event.stream_url
      const options = {
        starttime: event.starttime + offset - buffer,
        endtime: event.is_point ? event.starttime + offset + end_buffer : event.endtime + offset + end_buffer,
        source,
        mimeType: 'application/x-mpegURL',
        buffer: 0,
        autoplay: true
      }
      commit('SET_OPTIONS', options)
      Vue.nextTick(() => {
        commit('UPDATE_PLAYER_KEY')
        // Emit scroll event to center the event in the list
        if (event.event_id) {
          // Use a global event bus or window event instead
          window.dispatchEvent(new CustomEvent('scroll-to-event', {
            detail: { eventId: event.event_id }
          }))
        }
      })
    },
    nextEvent({ dispatch, getters }) {
      let i = getters.allFilteredEvents.findIndex(c => c.event_id == getters.openedEvent.event_id)

      if(getters.allFilteredEvents.length - 1 > i) {
        dispatch('openEvent', getters.allFilteredEvents[i + 1])
      } else {
        dispatch('openEvent', getters.allFilteredEvents[0])
      }
    },
    lastEvent({ dispatch, getters }) {
      let i = getters.allFilteredEvents.findIndex(c => c.event_id == getters.openedEvent.event_id)

      if(i > 0) {
        dispatch('openEvent', getters.allFilteredEvents[i - 1])
      } else {
        dispatch('openEvent', getters.allFilteredEvents[getters.allFilteredEvents.length - 1])
      }
    },
    loadMoreGames({ commit, state, dispatch }, { id, filter }) {
      const currentOffset = state.games_offset
      console.log('loadMoreGames - Current state:', {
        games_offset: state.games_offset,
        games_limit: state.games_limit,
        currentOffset: currentOffset
      })
      commit('SET_LOADING_MORE_GAMES', true)

      return dispatch('fetchGames', { id, filter, offset: currentOffset, limit: state.games_limit })
        .then(({ games, has_more_games }) => {
          console.log('loadMoreGames - API response:', {
            games_returned: games.length,
            has_more_games: has_more_games,
            offset_requested: currentOffset
          })

          commit('ADD_GAMES', games)
          commit('SET_GAMES_OFFSET', currentOffset + games.length)
          commit('SET_HAS_MORE_GAMES', has_more_games)
          commit('SET_LOADING_MORE_GAMES', false)
          return { games, has_more_games }
        })
        .catch(e => {
          commit('SET_LOADING_MORE_GAMES', false)
          throw e
        })
    },
    loadAllGames({ commit, state, dispatch }, { id, filter }) {
      commit('SET_LOADING_MORE_GAMES', true)

      const loadRemainingGames = (currentOffset) => {
        return dispatch('fetchGames', { id, filter, offset: currentOffset, limit: 9999 })
          .then(({ games, has_more_games }) => {
            if (games.length > 0) {
              commit('ADD_GAMES', games)
              commit('SET_GAMES_OFFSET', currentOffset + games.length)

              // If there are still more games, continue loading
              if (has_more_games) {
                return loadRemainingGames(currentOffset + games.length)
              }
            }

            commit('SET_HAS_MORE_GAMES', false)
            commit('SET_LOADING_MORE_GAMES', false)
            return { games, has_more_games }
          })
          .catch(e => {
            commit('SET_LOADING_MORE_GAMES', false)
            throw e
          })
      }

      return loadRemainingGames(state.games_offset)
    },
  },
  getters: {
    filters: state => state.filters,
    loading: state => state.loading,
    events: state => state.events,
    openedEvent: state => state.openedEvent,
    buffer: state => state.buffer,
    end_buffer: state => state.end_buffer,
    eventsFlat: (state, getters) => {
      return getters.events.map(v => {
        var video = {...v}
        delete video.clips
        return v.clips.map(clip => {
          return { ...clip, video }
        })
      }).flat()
    },
    eventById: (state, getters) => id => {
      return getters.eventsFlat.find(e => e.id == id)
    },
    filter: state => state.filter,
    event: state => state.event,
    all_events: state => {
      return Object.values(state.games).map(game => game['events']).flat();
    },
    games: state => state.games,
    player_key: state => state.player_key,
    options: state => state.options,
    selected_feed: state => state.selected_feed,
    secondaryFeedSyncOffset: () => event => {
      const offset = event?.second_video_sync_offset_seconds ?? event?.game?.second_video_sync_offset_seconds
      if(offset === null || offset === undefined || offset === '') return null
      return Number(offset)
    },
    secondaryFeedSource: () => event => {
      return event?.second_stream_url
        || event?.second_video_url
        || event?.second_video_live_url
        || event?.game?.second_stream_url
        || event?.game?.second_video_url
        || event?.game?.second_video_live_url
    },
    secondaryFeedAvailable: (_, getters) => event => {
      return getters.secondaryFeedSyncOffset(event) !== null && !!getters.secondaryFeedSource(event)
    },
    openedEventFeedOptions: (state, getters) => {
      if(!state.openedEvent) return []

      const options = [
        { text: 'Feed 1', value: 'primary' }
      ]

      if(getters.secondaryFeedAvailable(state.openedEvent)) {
        options.push({ text: 'Feed 2', value: 'secondary' })
      }

      return options
    },
    user_opened_event: state => state.user_opened_event,
    event_opened: (state) => event_id => {
      if (!state.openedEvent || !event_id) return false

      // Check multiple possible ID fields
      return state.openedEvent.event_id == event_id ||
             state.openedEvent.id == event_id ||
             state.openedEvent.event_id == event_id.toString() ||
             state.openedEvent.id == event_id.toString()
    },
    gameFilteredEvents: (state) => events => {
      // If quality_control_percentage_checked is set, select that percentage of events at random (seeded)
      const pct = parseInt(state.quality_control_percentage_checked)
      if (!isNaN(pct) && pct > 0 && pct < 100 && events && events.length > 0) {
        // Use the first event's game_id as the seed (or fallback to 1)
        const gameId = events[0]?.game_id || 1
        const rand = seededRandom(Number(String(gameId).replace(/\D/g, '')) || 1)
        // Shuffle events deterministically
        const eventsCopy = [...events]
        for (let i = eventsCopy.length - 1; i > 0; i--) {
          const j = Math.floor(rand() * (i + 1))
          ;[eventsCopy[i], eventsCopy[j]] = [eventsCopy[j], eventsCopy[i]]
        }
        const count = Math.max(1, Math.round(eventsCopy.length * pct / 100))
        const selected = eventsCopy.slice(0, count)
        console.log(`[QC] Events for game ${gameId}: original=${events.length}, selected=${selected.length} (${pct}%)`)
        return filterEvents(state.filter, selected || [])
      }
      return filterEvents(state.filter, events || [])
    },
    allFilteredEvents: (state, getters) => {
      return state.games.map(g => getters.gameFilteredEvents(g.events)).flat()
    },
    uniqueMapBaseIds: (_, getters) => {
      // from all filtered events, get unique map_base_ids
      return [...new Set(getters.allFilteredEvents.map(e => e.map_base_id))].filter(e => e)
    },
    opened_map_type: state => state.opened_map_type,
    maps_open: state => state.maps_open,
    currentMapPoints: (state, getters) => {
      return getters.allFilteredEvents.map(e => {
        return pointFromEvent(e, state.openedEvent?.event_id, getters.opened_map_type)
      }).filter(e => e)
    },
    loading_some: state => {
      return state.loading || state.games.some(g => g.loading)
    },
    // Pagination getters
    games_offset: state => state.games_offset,
    has_more_games: state => state.has_more_games,
    loading_more_games: state => state.loading_more_games,
    is_quality_control: state => state.is_quality_control,
    quality_control_view: state => state.quality_control_view,
    quality_control_event_key: state => state.quality_control_event_key,
    quality_control_percentage_checked: state => state.quality_control_percentage_checked,
    quality_control_analysis_phase: state => state.quality_control_analysis_phase
  }
}

export default filter